/**
 * 파일명 : evl0101m.tsx
 * 설명 : 인사평가 메인화면 - 모바일
 * 수정내역
 * **********************************************
 * Version    수정일자      수정자    수정내용
 * **********************************************
 * 1.0       2025-12-18   김나린    최초작성
 * 1.1       2026-01-22   조동현    모바일 버전 생성
 * 1.2       2026-03-05   이예원    디자인 개편
 */

/************************************************
 * Import 영역
 ************************************************/

import React, { useState, useMemo, useEffect } from "react";
/* Reducer */
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { Evl0101mAction } from "../../../redux/slice/evl/evl0101m_slice";
import { EVALUATION_TYPES, EvaluationType, StatsCardProps, Employee, EvaluationCycle, getEvaluationPeriods, getEvaluationDeadlines } from "../../../redux/models/evl/evl0101m_model";
import { Card, Input, Select, Button, Tag, Typography, Space, Row, Col, Tabs, Modal, message, Spin, Switch } from "antd";
import { FileTextOutlined, CheckCircleOutlined, LockOutlined, SearchOutlined, CloseOutlined, StopOutlined, EditOutlined, CloseCircleOutlined } from "@ant-design/icons";

import * as common from 'libs/common';
import * as commonProxy from 'libs/CommonProxy';
import { isMobile } from "react-device-detect";

const { Title, Text } = Typography;

// ====================================
// 통계 카드 컴포넌트
// ====================================

const StatsCard = ({ title, completed, total, deadline }: StatsCardProps) => {
  const incomplete = total - completed;
  
  return (
    <div style={{ flex: "1 1 0", minWidth: 160 }}>
      <Card 
        style={{ 
          textAlign: "center",
          border: "2px solid #bfbfbf",
        }}
        styles={{ body: { padding: "8px 6px" } }}
      >
        <Text strong style={{ fontSize: 13, display: "block", marginBottom: 4, color: "#000", whiteSpace: "nowrap" }}>
          {title}
        </Text>
        <div style={{ fontSize: 20, fontWeight: "bold", lineHeight: 1.2, margin: "2px 0" }}>
          {completed}/{total}
          <Text style={{ fontSize: 11, marginLeft: 6, color: "#ff4d4f", fontWeight: "normal" }}>
            (미완료 {incomplete})
          </Text>
        </div>
        <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4, color: "#000", whiteSpace: "nowrap" }}>
          마감: {deadline}
        </Text>
      </Card>
    </div>
  );
};

// ====================================
// 유틸리티 함수
// ====================================

/**
 * 기능 : getevlTypeMenu
 * 설명 : 평가구분별 메뉴 정의
 * param : type - 평가 타입 문자열
 * return : 메뉴 ID
 **/
export const getevlTypeMenu = (type: string): string => {
  switch (type) {
    case "업무서약서":
      return "MEVL2025003";
    case "인사평가-자기평가":
      return "MEVL2025006";
    case "인사평가-1차평가":
      return "MEVL2025006";
    case "인사평가-2차평가":
      return "MEVL2025006";
    case "프로젝트평가-팀원":
    case "04": // pjEvlType === "04" (프로젝트평가-팀원)
      return "MEVL2025007";
    case "프로젝트평가-PM":
    case "03": // pjEvlType === "03" (프로젝트평가-PM)
      return "MEVL2025008";
    default:
      return "MEVL2025001";
  }
};
/**
 * 기능 : 평가 페이지 열기
 * 설명 : 평가 상세 페이지를 열고 필요한 파라미터 전달
 * param : evaluation - 평가 데이터, selectedYear - 선택된 평가 연도, canEdit - 본인이 작성 가능한 평가인지 여부
 */
const openEvlPage = (evaluation: any, selectedYear: number, canEdit: boolean) => {
  const isInPeriod  = evaluation.evalPeriodYn === "Y" || evaluation.evalPeriodYn === true;
  const isCompleted = evaluation.evlStatus === "completed";

  let evlEnableYn   = "N"; // 기본값: readonly
  let evlEditableYn = "N"; // 평가 작성 가능 여부 (Y/N)

  if (canEdit) {
    // 본인이 작성 가능한 평가
    if (!isCompleted && isInPeriod) {
      // 3) 제출 안 함 + 기한 안 지남 → 작성 가능
      evlEnableYn   = "Y";
      evlEditableYn = "Y";
    } else if (isCompleted && isInPeriod) {
      evlEnableYn   = "N";
      evlEditableYn = "Y";
    }
     else {
      evlEnableYn = "N";
    }
    // 4) 제출 안 함 + 기한 지남 → 이 경우는 버튼이 비활성화되어 호출되지 않음
  } else {
    // 타인의 평가 (조회만)
    // 1) 제출 완료 + 기한 지남 → readonly
    // 2) 제출 완료 + 기한 안 지남 → readonly
    // 3,4) 제출 안 함 → 이 경우는 버튼이 비활성화되어 호출되지 않음
    evlEnableYn = "N";
  }

  const param = {
    menuId: getevlTypeMenu(evaluation.evlType),
    props: {
      evlId        : evaluation.evlId,
      evlType      : evaluation.evlType,
      empNo        : evaluation.empNo,
      hrMapId      : evaluation.evlId,
      pjNm         : evaluation.pjNm,
      pjCd         : evaluation.pjCd,
      evlEnableYn  : evlEnableYn,
      evlYear      : selectedYear,
      pjPmEmpNo    : evaluation.pjPmEmpNo, // 프로젝트평가자 empNo 추가
      evlEditableYn: evlEditableYn,        // 평가 작성 가능 여부
    },
  };
  
  common.openPage(param);
};

// ====================================
// 평가 타입을 문자열로 변환하는 헬퍼 함수
// ====================================
const getEvlTypeString = (type: EvaluationType): string => {
  switch (type) {
    case "work-commitment":
      return "업무서약서";
    case "self-evaluation":
      return "인사평가-자기평가";
    case "first-evaluation":
      return "인사평가-1차평가";
    case "second-evaluation":
      return "인사평가-2차평가";
    case "project-evaluation":
      return "프로젝트평가-팀원"; // 기본값, 필요시 PM으로 변경
    default:
      return "인사평가";
  }
};

const Evl0101m = (props: any) => {
  const dispatch = useAppDispatch();
  const { activeTab, selectedProjectId, selectedEmployeeId, evaluationCycles, loading } = useAppSelector((state) => state.Evl0101m);
  
  // ====================================
  // State 관리
  // ====================================
  const [searchQuery, setSearchQuery] = useState("");                                    // 검색어
  const [selectedDepartment, setSelectedDepartment] = useState<string>("all");           // 선택된 부서
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());    // 선택된 연도 (현재 연도)
  const [projectModalVisible, setProjectModalVisible] = useState(false);                 // 프로젝트평가 모달 표시 여부
  const [selectedEmployeeForModal, setSelectedEmployeeForModal] = useState<Employee | null>(null); // 모달에 표시할 직원 정보
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200); // 화면 너비
  const [employees, setEmployees] = useState<Employee[]>([]);                            // 백엔드에서 가져온 직원 데이터
  const [employeesLoading, setEmployeesLoading] = useState(false);                       // 직원 데이터 로딩 상태
  const [isAdminMode, setIsAdminMode] = useState(false);                                 // 관리자 모드 여부
  
  // ====================================
  // 현재 로그인한 사용자 정보
  // ====================================
  const loginEmpNo = common.getEmpNo();
  const isHrAdmin = common.isHrAdmin();
  const isHrAdminFlag = (isHrAdmin && isAdminMode) ? 1 : 0;

  // ====================================
  // 백엔드에서 평가 주기 데이터 조회
  // ====================================
  const fetchEvaluationCycles = () => {
    dispatch(Evl0101mAction.setLoading(true));

    const requestData = {
      // TODO: 필요한 요청 파라미터 추가
      // loginEmpNo: loginEmpNo,
    };

    commonProxy.callAsyncSvc(requestData, {
      txCd: 'TREVLR0001', // 평가주기 조회
      success: (responseData: any) => {
        // 백엔드 응답: { data: { EvlCycleItems: [...] } }
        // 날짜 형식 변환 헬퍼 (YYYYMMDD -> YYYY-MM-DD)
        const formatDate = (dateStr: string): string => {
          if (!dateStr || dateStr.length !== 8) return dateStr;
          return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
        };
        
        const rawCycles = responseData.data?.EvlCycleItems || 
                          responseData.EvlCycleItems || [];
        
        const cycles: EvaluationCycle[] = rawCycles.map((item: any) => ({
          evlYear   : typeof item.evlYear === 'string' ? parseInt(item.evlYear, 10) : item.evlYear,
          hrEvlStDt : formatDate(item.hrEvlStDt),
          slfEvlEdDt: formatDate(item.slfEvlEdDt),
          eval1EdDt : formatDate(item.eval1EdDt),
          eval2EdDt : formatDate(item.eval2EdDt),
          pjtEvlEdDt: formatDate(item.pjtEvlEdDt),
          bizPlgStDt: formatDate(item.bizPlgStDt),
          bizPlgEdDt: formatDate(item.bizPlgEdDt),
        }));
        
        if (cycles.length > 0) {
          // 현재 연도가 cycles에 없으면 추가
          const currentYear = new Date().getFullYear(); // 현재 연도
          const hasCurrentYear = cycles.some(cycle => cycle.evlYear === currentYear);
          
          if (!hasCurrentYear) {
            // 현재 연도 데이터 추가 (기본값 설정)
            cycles.push({
              evlYear   : currentYear,
              hrEvlStDt : `${currentYear}-01-01`,
              slfEvlEdDt: `${currentYear}-12-31`,
              eval1EdDt : `${currentYear}-12-31`,
              eval2EdDt : `${currentYear}-12-31`,
              pjtEvlEdDt: `${currentYear}-12-31`,
              bizPlgStDt: `${currentYear}-01-01`,
              bizPlgEdDt: `${currentYear}-12-31`,
            });
            // 연도 순으로 정렬 (최신 연도가 위로)
            cycles.sort((a, b) => b.evlYear - a.evlYear);
          }
          
          dispatch(Evl0101mAction.setEvaluationCycles(cycles));
        } else {
          message.info('평가 주기 데이터가 없습니다.');
        }
        dispatch(Evl0101mAction.setLoading(false));
      },
      error: (errorMsg: string) => {
        message.error(errorMsg || '평가 주기 데이터 조회에 실패했습니다.');
        dispatch(Evl0101mAction.setLoading(false));
      }
    });
  };

  // ====================================
  // 백엔드에서 직원 평가 데이터 조회 (평가대상 조회)
  // ====================================
  const fetchEmployeeData = () => {
    setEmployeesLoading(true); // 로딩 시작
    
    const requestData = {
      loginEmpNo: loginEmpNo,
      evlYear   : selectedYear,
      isHrAdmin : isAdminMode ? 1 : 0,
    };

    commonProxy.callAsyncSvc(requestData, {
      txCd: 'TREVLR0010',
      success: (responseData: any) => {
        const rawItems           = responseData.data?.EvlMainItems || responseData.EvlMainItems || [];
        const periodFromResponse = responseData.data?.period || responseData.period || null;
        
        if (rawItems.length === 0) {
          setEmployees([]);
          setEmployeesLoading(false); // 로딩 종료
          return;
        }

        // ====================================
        // TREVLR0010에서 바로 배열 데이터 처리
        // ====================================
        const formatDate = (dateStr: string): string => {
          if (!dateStr || dateStr.length !== 8) return dateStr;
          return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
        };

        // empNo별로 그룹화 (HR 정보와 프로젝트 정보 분리)
        const groupedByEmpNo = rawItems.reduce((acc: any, item: any) => {
          const empNo = item.empNo;
          if (!acc[empNo]) {
            acc[empNo] = { hrInfo: null, projects: [] };
          }
          if (!acc[empNo].hrInfo) {
            acc[empNo].hrInfo = item;
          }
          if (item.pjMapId) {
            acc[empNo].projects.push(item);
          }
          return acc;
        }, {});

        // Employee 객체 생성
        const allEmployeeData: Employee[] = Object.values(groupedByEmpNo).map((group: any) => {
          const hrInfo = group.hrInfo;
          
          // period 필드에 따라 hasHrEvaluation 결정
          // - period === "HR": 전체 인사평가 표시 (hasHrEvaluation = 'Y')
          // - period === "PLG": 업무서약서만 표시 (hasHrEvaluation = 'N')
          const hasHrEvaluation = periodFromResponse === 'HR' ? 'Y' : 'N';
          
          return {
            evlId            : hrInfo.hrMapId || hrInfo.empNo,
            empNo            : hrInfo.empNo,
            name             : hrInfo.empNm,
            position         : hrInfo.empPos,
            department       : hrInfo.empDept,
            hasHrEvaluation  : hasHrEvaluation,
            period           : periodFromResponse,
            firstEvaluatorId : hrInfo.eval1EmpNo,
            secondEvaluatorId: hrInfo.eval2EmpNo,
            evaluations      : {
              bizPlgEvaluation  : hrInfo.bizPlgSubYn || 'N',
              selfEvaluation    : hrInfo.slfSubYn || 'N',
              firstEvaluation   : hrInfo.eval1SubYn || 'N',
              secondEvaluation  : hrInfo.eval2SubYn || 'N',
              allPjtEvaluations : group.projects.length === 0 ? 'Y' : (group.projects.every((p: any) => p.pjSubYn === 'Y') ? 'Y' : 'N'),
              projectEvaluations: group.projects.map((proj: any) => {
                // 프로젝트 평가자는 항상 PM (pjPmEmpNo)
                // - "03" (프로젝트평가-PM): PM이 PM 역할을 한 팀원을 평가
                // - "04" (프로젝트평가-팀원): PM이 팀원을 평가
                const evaluatorId = proj.pjPmEmpNo;
                
                return {
                  evlId              : proj.pjMapId,
                  evlType            : proj.pjEvlType,
                  pjCd               : proj.pjCd,
                  pjNm               : proj.pjNm,
                  customerName       : proj.pjCusNm,
                  isCompleted        : proj.pjSubYn || 'N',
                  status             : (proj.pjSubYn || 'N') === 'Y' ? 'completed' : 'in-progress',
                  startDate          : formatDate(proj.pjEvlStDt),
                  endDate            : formatDate(proj.pjEvlEdDt),
                  evaluatorId        : evaluatorId,
                  evaluatorName      : proj.pjPmEmpNm,
                  evaluatorPosition  : proj.pjPmEmpPos,
                  evaluatorDepartment: proj.pjPmEmpDept,
                };
              }),
            },
            evaluationPeriods: {
              bizPlgEvaluation: { 
                startDate: formatDate(hrInfo.bizPlgStDt), 
                endDate  : formatDate(hrInfo.bizPlgEdDt) 
              },
              selfEvaluation: { 
                startDate: formatDate(hrInfo.slfEvalStDt), 
                endDate  : formatDate(hrInfo.slfEvlEdDt) 
              },
              firstEvaluation: { 
                startDate: formatDate(hrInfo.eval1EvlStDt), 
                endDate  : formatDate(hrInfo.eval1EvlEdDt) 
              },
              secondEvaluation: { 
                startDate: formatDate(hrInfo.eval2EvlStDt), 
                endDate  : formatDate(hrInfo.eval2EvlEdDt) 
              },
            },
          };
        });

        // ====================================
        // 로그인한 사용자와 관련된 데이터만 필터링
        // ====================================
        // 관리자 모드: 모든 직원 포함 (필터링 없음)
        const filteredEmployees = isAdminMode ? allEmployeeData : allEmployeeData.filter((employee) => {
          // PLG 기간(업무서약서 시즌): 모든 직원 포함
          if (employee.period === 'PLG') {
            return true;
          }
          
          // HR 기간(전체 인사평가): 권한이 있는 직원만
          // 1. 본인이 평가 대상자인 경우
          const isSelf             = employee.empNo            === loginEmpNo;
          
          // 2. 본인이 1차 평가자인 경우
          const isFirstEvaluator   = employee.firstEvaluatorId === loginEmpNo;
          
          // 3. 본인이 2차 평가자인 경우
          const isSecondEvaluator  = employee.secondEvaluatorId === loginEmpNo;
          
          // 4. 본인이 프로젝트 평가자인 경우
          const isProjectEvaluator = employee.evaluations.projectEvaluations.some(
            (proj) => proj.evaluatorId === loginEmpNo
          );
          
          return isSelf || isFirstEvaluator || isSecondEvaluator || isProjectEvaluator;
        });
        
        setEmployees(filteredEmployees);
        setEmployeesLoading(false); // 로딩 종료
      },
      error: (errorMsg: string) => {
        message.error(errorMsg || '평가 대상자 조회에 실패했습니다.');
        setEmployees([]);
        setEmployeesLoading(false); // 로딩 종료
      }
    });
  };

  // ====================================
  // 컴포넌트 마운트 시 평가 주기 데이터 로드 및 탭 초기화
  // ====================================
  useEffect(() => {
    fetchEvaluationCycles();
    dispatch(Evl0101mAction.setActiveTab('hr')); // 컴포넌트 마운트 시 항상 인사평가 탭으로
  }, []);

  // ====================================
  // 연도 변경 시 직원 데이터 재조회
  // ====================================
  useEffect(() => {
    if (selectedYear) {
      fetchEmployeeData();
      dispatch(Evl0101mAction.setActiveTab('hr')); // 연도 변경 시 항상 인사평가 탭으로
    }
  }, [selectedYear]);
  
  // ====================================
  // 관리자 모드 변경 시 직원 데이터 재조회
  // ====================================
  useEffect(() => {
    if (selectedYear) {
      fetchEmployeeData();
    }
  }, [isAdminMode]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // 현재 날짜 (문자열과 Date 객체 모두 제공)
  const currentDateStr = new Date().toISOString().split('T')[0];
  const currentDate    = new Date(currentDateStr);

  // ====================================
  // 선택된 연도의 평가 주기 데이터
  // ====================================
  const evaluationPeriods = useMemo(() => {
    if (evaluationCycles.length === 0) {
      // 데이터가 아직 로드되지 않았을 때 기본값 반환
      return {
        "work-commitment"   : { startDate: "", endDate: "" },
        "self-evaluation"   : { startDate: "", endDate: "" },
        "first-evaluation"  : { startDate: "", endDate: "" },
        "second-evaluation" : { startDate: "", endDate: "" },
        "project-evaluation": { startDate: "", endDate: "" }
      };
    }
    
    const cycle = evaluationCycles.find(c => c.evlYear === selectedYear);
    if (!cycle) {
      console.error(`평가 주기 데이터를 찾을 수 없습니다: ${selectedYear}년`);
      // 첫 번째 사용 가능한 주기 데이터가 있으면 사용, 없으면 기본값
      if (evaluationCycles.length > 0) {
        return getEvaluationPeriods(evaluationCycles[0]);
      }
      return {
        "work-commitment"   : { startDate: "", endDate: "" },
        "self-evaluation"   : { startDate: "", endDate: "" },
        "first-evaluation"  : { startDate: "", endDate: "" },
        "second-evaluation" : { startDate: "", endDate: "" },
        "project-evaluation": { startDate: "", endDate: "" }
      };
    }
    return getEvaluationPeriods(cycle);
  }, [selectedYear, evaluationCycles]);

  const evaluationDeadlines = useMemo(() => {
    if (evaluationCycles.length === 0) {
      // 데이터가 아직 로드되지 않았을 때 기본값 반환
      return {
        "work-commitment"   : "-",
        "self-evaluation"   : "-",
        "first-evaluation"  : "-",
        "second-evaluation" : "-",
        "project-evaluation": "-"
      };
    }
    
    const cycle = evaluationCycles.find(c => c.evlYear === selectedYear);
    if (!cycle) {
      console.error(`평가 마감일 데이터를 찾을 수 없습니다: ${selectedYear}년`);
      // 첫 번째 사용 가능한 주기 데이터가 있으면 사용, 없으면 기본값
      if (evaluationCycles.length > 0) {
        return getEvaluationDeadlines(evaluationCycles[0]);
      }
      return {
        "work-commitment"   : "-",
        "self-evaluation"   : "-",
        "first-evaluation"  : "-",
        "second-evaluation" : "-",
        "project-evaluation": "-"
      };
    }
    return getEvaluationDeadlines(cycle);
  }, [selectedYear, evaluationCycles]);

  // ====================================
  // 화면 크기 감지
  // ====================================
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ====================================
  // 프로젝트 선택 시 자동으로 평가 폼으로 이동
  // ====================================
  // Redux를 통한 탭 전환 (navigate 제거)
  // ====================================
  useEffect(() => {
    if (selectedProjectId && selectedEmployeeId) {
      // 프로젝트평가 탭으로 전환만 수행 (navigate 제거)
      // 이동 후 선택 상태 초기화
      dispatch(Evl0101mAction.clearSelection());
    }
  }, [selectedProjectId, selectedEmployeeId, dispatch]);

  // ====================================
  // 현재 날짜
  // ====================================
  const today = new Date(); // 현재 날짜
  
  
  // ====================================
  // 평가자 ID를 이름으로 매핑하는 함수
  // ====================================
  const getEvaluatorName = (evaluatorId: string): string => {
    const evaluator = employees.find(emp => emp.evlId === evaluatorId);
    return evaluator ? evaluator.name : "미지정";
  };
  
  // ====================================
  // 평가자 전체 정보 가져오기
  // ====================================
  const getEvaluatorInfo = (evaluatorId: string) => {
    const evaluator = employees.find(emp => emp.evlId === evaluatorId);
    return evaluator 
      ? {
          name      : evaluator.name,
          position  : evaluator.position,
          department: evaluator.department,
        }
      : { name: "미지정", position: "-", department: "-" };
  };
  
  // ====================================
  // 평가 유형 매핑
  // ====================================
  // evaluations 객체의 key와 평가 타입을 매핑
  const evaluationKeys: Array<{
    key: keyof Omit<Employee["evaluations"], "projectEvaluations" | "allPjtEvaluations">;
    type: EvaluationType;
  }> = [
    { key: "bizPlgEvaluation", type: "work-commitment"   },  // 업무서약서
    { key: "selfEvaluation"  , type: "self-evaluation"   },  // 자기평가
    { key: "firstEvaluation" , type: "first-evaluation"  },  // 1차평가
    { key: "secondEvaluation", type: "second-evaluation" },  // 2차평가
  ];

  // ====================================
  // 부서 목록 추출 (중복 제거 및 정렬)
  // ====================================
  const departments = useMemo(() => {
    const depts = Array.from(new Set(employees.map((emp) => emp.department)));
    return depts.sort();
  }, [employees]);

  // ====================================
  // 필터링된 직원 목록
  // ====================================
  // 검색어, 부서, 평가 필터에 따라 직원 목록 필터링
  // + 현재 로그한 사원이 해당 직원의 평가 권한이 있는지 확인
  const filteredEmployees = useMemo(() => {
    return employees.filter((employee) => {
      // 이름으로 검색
      const matchesSearch = (employee.name || "").toLowerCase().includes(searchQuery.toLowerCase());
      
      // 부서 필터
      const matchesDepartment = selectedDepartment === "all" || employee.department === selectedDepartment;
      
      // ====================================
      // 관리자 모드: 권한 체크 없이 모든 직원 표시
      // ====================================
      if (isAdminMode) {
        return matchesSearch && matchesDepartment;
      }
      
      // PLG 기간(업무서약서 시즌): 모든 직원 표시 (권한 체크 없음)
      if (employee.period === 'PLG') {
        return matchesSearch && matchesDepartment;
      }
      
      // ====================================
      // HR 기간: 평가 권한 확인
      // ====================================
      // 현재 사용자가 이 직원에 대해 어떤 평가든 할 수 있는지 확인
      const isCurrentEmployee = loginEmpNo === employee.empNo;             // 본인 여부
      const isFirstEvaluator  = loginEmpNo === employee.firstEvaluatorId;  // 1차 평가자 여부
      const isSecondEvaluator = loginEmpNo === employee.secondEvaluatorId; // 2차 평가자 여부
      
      // 프로젝트 평가자 여부 확인
      const hasProjectEvaluationRole = employee.evaluations.projectEvaluations.some(
        (project) => !project.evaluatorId || project.evaluatorId === loginEmpNo
      );
      // evaluatorId가 없으면 현재 사용자를 평가자로 간주 (기존 데이터 호환)
      // evaluatorId가 있으면 loginEmpNo와 일치하는지 확인
      
      // 하나라도 평가 권한이 있으면 표시
      const hasAnyEvaluationRole = isCurrentEmployee || isFirstEvaluator || isSecondEvaluator || hasProjectEvaluationRole;
      
      return matchesSearch && matchesDepartment && hasAnyEvaluationRole;
    });
  }, [searchQuery, selectedDepartment, employees, loginEmpNo, isAdminMode]);

  // ====================================
  // 프로젝트별 그룹화
  // ====================================
  // 프로젝트명을 키로, 해당 프로젝트에 참여한 직원들을 값으로 하는 객체 생성
  // + 현재 사용자가 해당 프로젝트의 평가자인 경우만 포함
  const projectGroups = useMemo(() => {
    const groups: Record<string, Array<{
      employee: Employee;
      project : Employee["evaluations"]["projectEvaluations"][0];
    }>> = {};

    filteredEmployees.forEach((employee) => {
      employee.evaluations.projectEvaluations.forEach((project) => {
        // 관리자 모드: 모든 프로젝트 표시
        // 일반 모드: 로그인한 사용자가 이 프로젝트의 평가자인 경우만 표시
        const isProjectEvaluator = isAdminMode || project.evaluatorId === loginEmpNo;
        
        if (isProjectEvaluator) {
          if (!groups[project.pjNm]) {
            groups[project.pjNm] = [];
          }
          groups[project.pjNm].push({ employee, project });
        }
      });
    });

    return groups;
  }, [filteredEmployees, loginEmpNo, isAdminMode]);

  // ====================================
  // 전체 진행률 계산
  // ====================================
  // 기본 인사평가(4개) + 프로젝트 평가의 완료율을 계산
  const getOverallProgress = (evaluations: Employee["evaluations"]) => {
    // 기본 인사평가 4개
    const basicEvals = [
      evaluations.bizPlgEvaluation,
      evaluations.selfEvaluation,
      evaluations.firstEvaluation,
      evaluations.secondEvaluation,
    ];
    const completedBasic = basicEvals.filter(v => v === "Y").length;
    
    // 프로젝트 평가
    const completedProjects = evaluations.projectEvaluations.filter(p => p.isCompleted === "Y").length;
    const totalProjects     = evaluations.projectEvaluations.length;
    
    // 전체 완료율
    const totalCompleted = completedBasic + completedProjects;
    const totalCount     = 4 + totalProjects;
    
    return totalCount > 0 ? Math.round((totalCompleted / totalCount) * 100) : 0;
  };

  // ====================================
  // 평가 작성 가능 여부 확인
  // ====================================
  // 순차적 의존성 및 프로젝트 평가 완료 + 평가 기간 확인
  // - 업무서약서, 자기평가, 프로젝트평가: 기간 내에 작성 가능
  // - 1차평가: 자기평가 완료 + 모든 프로젝트 평가 완료 + 1차평가 시작일 도래 시 작성 가능
  // - 2차평가: 1차평가 완료 + 2차평가 시작일 도래 시 작성 가능
  
  // EvaluationType을 evaluationPeriods 키로 변환
  const getEvaluationPeriodKey = (type: EvaluationType): keyof Employee['evaluationPeriods'] | null => {
    switch (type) {
      case "work-commitment":
        return "bizPlgEvaluation";
      case "self-evaluation":
        return "selfEvaluation";
      case "first-evaluation":
        return "firstEvaluation";
      case "second-evaluation":
        return "secondEvaluation";
      case "project-evaluation":
        return null; // 프로젝트 평가는 개별 관리
      default:
        return null;
    }
  };
  
  const isEvaluationAvailable = (
    type: EvaluationType,
    employee: Employee
  ) => {
    const evaluations = employee.evaluations;
    
    // 날짜 비교 함수 (YYYY-MM-DD 형식)
    const isDateInRange = (startDate: string, endDate: string) => {
      return currentDateStr >= startDate && currentDateStr <= endDate;
    };
    
    const hasStarted = (startDate: string) => {
      return currentDateStr >= startDate;
    };
    
    // 개인별 평가 기간 사용
    const periodKey = getEvaluationPeriodKey(type);
    const periods = periodKey ? employee.evaluationPeriods[periodKey] : undefined;
    
    switch (type) {
      case "work-commitment":
        // 업무서약서: 기간 내에만 작성 가능
        return periods ? isDateInRange(periods.startDate, periods.endDate) : true;
      case "self-evaluation":
        // 자기평가: 기간 내에만 작성 가능
        return periods ? isDateInRange(periods.startDate, periods.endDate) : true;
      case "first-evaluation":
        // 1차평가: 자기평가 완료 + 모든 프로젝트 평가 완료 + 1차평가 시작일 도래
        const prerequisitesFirst = evaluations.selfEvaluation === "Y" && evaluations.allPjtEvaluations === "Y";
        const firstStarted       = periods ? hasStarted(periods.startDate) : true;
        return prerequisitesFirst && firstStarted;
      case "second-evaluation":
        // 2차평가: 1차평가 완료 + 2차평가 시작일 도래
        const prerequisitesSecond = evaluations.firstEvaluation === "Y";
        const secondStarted       = periods ? hasStarted(periods.startDate) : true;
        return prerequisitesSecond && secondStarted;
      case "project-evaluation":
        // 프로젝트 평가: 기간 내에만 작성 가능
        return true;  // 프로젝트평가는 개별 프로젝트마다 기간이 다르므로 여기서는 체크하지 않음
      default:
        return false;
    }
  };

  // ====================================
  // 평가 상태 확인 헬퍼 함수들
  // ====================================
  
  // 평가 기간이 도래했는지 확인 (개인별 평가 기간 사용)
  const hasEvaluationPeriodStarted = (type: EvaluationType, employee: Employee) => {
    const periodKey = getEvaluationPeriodKey(type);
    const periods   = periodKey ? employee.evaluationPeriods[periodKey] : undefined;
    return periods ? currentDateStr >= periods.startDate : true;
  };

  // 평가 기간이 지났는지 확인 (개인별 평가 기간 사용)
  const isEvaluationExpired = (type: EvaluationType, employee: Employee) => {
    const periodKey = getEvaluationPeriodKey(type);
    const periods   = periodKey ? employee.evaluationPeriods[periodKey] : undefined;
    return periods ? currentDateStr > periods.endDate : false;
  };

  // 선행 조건이 충족되었는지 확인
  const arePrerequisitesMet = (
    type    : EvaluationType,
    employee: Employee
  ) => {
    const evaluations = employee.evaluations;
    
    switch (type) {
      case "work-commitment":
      case "self-evaluation":
      case "project-evaluation":
        return true; // 선행 조건 없음
      case "first-evaluation":
        return evaluations.selfEvaluation === "Y" && evaluations.allPjtEvaluations === "Y";
      case "second-evaluation":
        return evaluations.firstEvaluation === "Y";
      default:
        return false;
    }
  };

  // 평가 상태를 계산하는 함수
  // 반환: "completed" | "editable" | "expired" | "in-progress" | "waiting"
  const getEvaluationStatus = (
    type      : EvaluationType,
    employee  : Employee,
    isComplete: string
  ): "completed" | "editable" | "expired" | "in-progress" | "waiting" => {
    // 완료됨
    if (isComplete === "Y") {
      return "completed";
    }

    const periodStarted    = hasEvaluationPeriodStarted(type, employee);
    const periodExpired    = isEvaluationExpired(type, employee);
    const prerequisitesMet = arePrerequisitesMet(type, employee);

    // 미작성: 마감일이 지났는데도 제출하지 않음
    if (periodExpired) {
      return "expired";
    }

    // 작성 가능: 평가 기간 내 + 선행 조건 충족
    if (periodStarted && prerequisitesMet) {
      return "editable";
    }

    // 진행중: 선행 평가가 모두 완료되었고, 해당 평가의 시작일시가 도래한 경우
    if (prerequisitesMet && periodStarted) {
      return "in-progress";
    }

    // 대기: 선행 평가 중 하나라도 완료되지 않았거나, 선행 평가가 모두 완료되었더라도 해당 평가의 기간이 도래하지 않은 경우
    return "waiting";
  };

  // ====================================
  // 프로젝트 평가 통계
  // ====================================
  // 전체 프로젝트 평가의 완료/미완료 통계
  const projectStats = useMemo(() => {
    let totalProjects       = 0;
    let completedProjects   = 0;
    let incompleteEmployees = 0;

    employees.forEach((emp) => {
      const projects     = emp.evaluations.projectEvaluations;
      totalProjects     += projects.length;
      completedProjects += projects.filter(p => p.isCompleted === "Y").length;
      if (projects.some(p => p.isCompleted === "N")) {
        incompleteEmployees++;
      }
    });

    return { totalProjects, completedProjects, incompleteEmployees };
  }, [employees]);

  // ====================================
  // 인사평가 탭용 프로젝트 평가 통계
  // ====================================
  // 내가 인사평가 해야 하는 사람들이 받아야 하는 프로젝트평가 건수
  const basicEvalProjectStats = useMemo(() => {
    let totalProjects       = 0;
    let completedProjects   = 0;

    // 관리자 모드: 모든 직원의 프로젝트평가 카운트
    if (isAdminMode) {
      employees.forEach((emp) => {
        if (emp.hasHrEvaluation === "N") return;
        
        const projects     = emp.evaluations.projectEvaluations;
        totalProjects     += projects.length;
        completedProjects += projects.filter(p => p.isCompleted === "Y").length;
      });
    } else {
      // 일반 모드: 내가 1차 또는 2차 평가자인 직원들의 프로젝트평가만 카운트
      employees.forEach((emp) => {
        if (emp.hasHrEvaluation === "N") return;
        
        const isFirstEvaluator  = loginEmpNo === emp.firstEvaluatorId;
        const isSecondEvaluator = loginEmpNo === emp.secondEvaluatorId;
        
        if (isFirstEvaluator || isSecondEvaluator) {
          const projects     = emp.evaluations.projectEvaluations;
          totalProjects     += projects.length;
          completedProjects += projects.filter(p => p.isCompleted === "Y").length;
        }
      });
    }

    return { totalProjects, completedProjects };
  }, [employees, loginEmpNo, isAdminMode]);

  // ====================================
  // 프로젝트평가 탭용 통계
  // ====================================
  // 내가 프로젝트평가를 해야 하는 건수
  const myProjectStats = useMemo(() => {
    let totalProjects     = 0;
    let completedProjects = 0;

    Object.values(projectGroups).forEach((items) => {
      items.forEach(({ project }) => {
        totalProjects++;
        if (project.isCompleted === "Y") {
          completedProjects++;
        }
      });
    });

    return { totalProjects, completedProjects };
  }, [projectGroups]);

  // ====================================
  // 기본 인사평가 대상자 수 (평가 유형별)
  // ====================================
  // 각 평가 유형별로 대상 인원을 계산
  const evaluationTargets = useMemo(() => {
    const targets = {
      bizPlgEvaluation: [] as Employee[],
      selfEvaluation  : [] as Employee[],
      firstEvaluation : [] as Employee[],
      secondEvaluation: [] as Employee[],
    };

    employees.forEach((emp) => {
      const isCurrentEmployee = loginEmpNo === emp.empNo;
      const isFirstEvaluator  = loginEmpNo === emp.firstEvaluatorId;
      const isSecondEvaluator = loginEmpNo === emp.secondEvaluatorId;

      // PLG 기간: 업무서약서만 처리
      if (emp.period === 'PLG') {
        // PLG 기간에는 모든 직원을 통계에 포함
        targets.bizPlgEvaluation.push(emp);
        return; // PLG 기간에는 나머지 평가 제외
      }

      // 관리자 모드: 모든 직원을 모든 평가 통계에 포함
      if (isAdminMode) {
        targets.bizPlgEvaluation.push(emp);
        targets.selfEvaluation.push(emp);
        targets.firstEvaluation.push(emp);
        targets.secondEvaluation.push(emp);
        return;
      }

      // HR 기간: hasHrEvaluation 체크
      if (emp.hasHrEvaluation === "N") {
        return; // 기본 인사평가 대상자만
      }

      // 업무서약서: 본인 + 내가 1차 또는 2차 평가자인 모든 인원
      if (isCurrentEmployee || isFirstEvaluator || isSecondEvaluator) {
        targets.bizPlgEvaluation.push(emp);
      }

      // 자기평가: 본인 + 내가 1차평가자 + 내가 2차평가자
      if (isCurrentEmployee || isFirstEvaluator || isSecondEvaluator) {
        targets.selfEvaluation.push(emp);
      }

      // 1차평가: 내가 1차평가자 + 내가 2차평가자
      if (isFirstEvaluator || isSecondEvaluator) {
        targets.firstEvaluation.push(emp);
      }

      // 2차평가: 내가 2차평가자인 인원만
      if (isSecondEvaluator) {
        targets.secondEvaluation.push(emp);
      }
    });

    return targets;
  }, [employees, loginEmpNo, isAdminMode]);

  // 데이터가 없는 경우
  if (evaluationCycles.length === 0) {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Card>
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Text type="secondary">평가주기 데이터가 없습니다.</Text>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f5f5f5", marginTop: 30 }}>
      {/* ====================================
          Header - 연도 선택
          ==================================== */}
      <div style={{ backgroundColor: "#fff", borderBottom: "1px solid #f0f0f0", padding: "12px 0" }}>
        <div style={{ maxWidth: 1400, margin: "0 auto", padding: "0 16px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Space align="center" size={12}>
              <Select
                value={selectedYear}
                onChange={setSelectedYear}
                style={{ width: 120 }}
                size="middle"
                options={evaluationCycles.map(cycle => ({
                  value: cycle.evlYear,
                  label: `${cycle.evlYear}년`
                }))}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                인사평가
              </Text>
            </Space>
            
            {/* 관리자인 경우 모드 전환 버튼 표시 */}
            {isHrAdmin && (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 12px",
                  borderRadius: 6,
                  backgroundColor: isAdminMode ? "#e6f7ff" : "#f5f5f5",
                  border: isAdminMode ? "1.5px solid #1890ff" : "1.5px solid #d9d9d9",
                  transition: "all 0.3s ease",
                }}
              >
                <Switch
                  size="small"
                  checked={isAdminMode}
                  onChange={(checked) => {
                    setIsAdminMode(checked);
                  }}
                  style={{
                    backgroundColor: isAdminMode ? "#1890ff" : undefined,
                  }}
                />
                <Text
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: isAdminMode ? "#1890ff" : "#595959",
                    width: 75,
                    textAlign: "left",
                  }}
                >
                  {isAdminMode ? "관리자 모드" : "일반 모드"}
                </Text>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ====================================
          Main Content
          ==================================== */}
      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "16px" }}>
        {/* ====================================
            피평가자 목록 섹션
            ==================================== */}
        {employeesLoading ? (
          // 로딩 중
          <Card size="small">
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <Spin size="large" tip="평가 데이터를 불러오는 중입니다..." />
            </div>
          </Card>
        ) : filteredEmployees.length === 0 ? (
          // 검색 결과 없음
          <Card size="small">
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <Text type="secondary">검색 결과가 없습니다.</Text>
            </div>
          </Card>
        ) : (
          <Tabs
            activeKey={activeTab === "hr" ? "basic" : "project"}
            onChange={(key) => dispatch(Evl0101mAction.setActiveTab(key === "basic" ? "hr" : "project"))}
            items={[
              {
                key: "basic",
                label: "인사평가",
                children: (
                  <Space direction="vertical" style={{ width: "100%" }} size={8}>
                    {/* ====================================
                        인사평가 탭 통계 카드
                        ==================================== */}
                    <div 
                      style={{ 
                        overflowX: "auto",
                        overflowY: "hidden"
                      }}
                    >
                      <div style={{ display: "flex", gap: 8, minWidth: "min-content" }}>
                        {/* 업무서약서 통계 */}
                        <StatsCard
                          title={EVALUATION_TYPES[evaluationKeys[0].type]}
                          completed={evaluationTargets.bizPlgEvaluation.filter(emp => emp.evaluations.bizPlgEvaluation === "Y").length}
                          total={evaluationTargets.bizPlgEvaluation.length}
                          deadline={evaluationDeadlines[evaluationKeys[0].type]}
                        />
                        
                        {/* PLG 기간이 아닐 때만 자기평가 ~ 2차평가 통계 표시 */}
                        {employees.length > 0 && employees[0].period !== 'PLG' ? (
                          <>
                            {/* 자기평가 통계 */}
                            <StatsCard
                              title={EVALUATION_TYPES[evaluationKeys[1].type]}
                              completed={evaluationTargets.selfEvaluation.filter(emp => emp.evaluations.selfEvaluation === "Y").length}
                              total={evaluationTargets.selfEvaluation.length}
                              deadline={evaluationDeadlines[evaluationKeys[1].type]}
                            />

                            {/* 프로젝트평가 통계 (인사평가 대상자 기준) */}
                            <StatsCard
                              title="프로젝트평가"
                              completed={basicEvalProjectStats.completedProjects}
                              total={basicEvalProjectStats.totalProjects}
                              deadline={evaluationDeadlines["project-evaluation"]}
                            />

                            {/* 1차평가 통계 */}
                            <StatsCard
                              title={EVALUATION_TYPES[evaluationKeys[2].type]}
                              completed={evaluationTargets.firstEvaluation.filter(emp => emp.evaluations.firstEvaluation === "Y").length}
                              total={evaluationTargets.firstEvaluation.length}
                              deadline={evaluationDeadlines[evaluationKeys[2].type]}
                            />

                            {/* 2차평가 통계 */}
                            <StatsCard
                              title={EVALUATION_TYPES[evaluationKeys[3].type]}
                              completed={evaluationTargets.secondEvaluation.filter(emp => emp.evaluations.secondEvaluation === "Y").length}
                              total={evaluationTargets.secondEvaluation.length}
                              deadline={evaluationDeadlines[evaluationKeys[3].type]}
                            />
                          </>
                        ) : (
                          <>
                            {/* PLG 기간: 빈 공간 추가로 프로젝트평가 탭과 동일한 크기 유지 */}
                            <div style={{ flex: "1 1 0", minWidth: 160 }} />
                            <div style={{ flex: "1 1 0", minWidth: 160 }} />
                            <div style={{ flex: "1 1 0", minWidth: 160 }} />
                            <div style={{ flex: "1 1 0", minWidth: 160 }} />
                          </>
                        )}
                      </div>
                    </div>

                    {/* ====================================
                        기본 인사평가 대상자 카드
                        ==================================== 
                        - hasHrEvaluation이 true인 직원들
                        - 업무서약서 ~ 2차평가까지 표시
                        - 로그인한 사용자의 역할에 따라 버튼 권한 제어
                    */}
                    <Row gutter={[8, 8]}>
              {filteredEmployees.filter(emp => {
                // 관리자 모드: 모든 직원 표시
                if (isAdminMode) {
                  return true;
                }
                
                // PLG 기간(업무서약서 시즌): 모든 직원 표시
                if (emp.period === 'PLG') {
                  return true;
                }
                
                // HR 기간(전체 인사평가): 본인이거나 1차/2차 평가자인 경우만
                const isSelf            = emp.empNo === loginEmpNo;
                const isFirstEvaluator  = emp.firstEvaluatorId === loginEmpNo;
                const isSecondEvaluator = emp.secondEvaluatorId === loginEmpNo;
                
                return (isSelf || isFirstEvaluator || isSecondEvaluator);
              }).sort((a, b) => a.name.localeCompare(b.name, 'ko-KR')).map((employee) => {
                const overallProgress = getOverallProgress(employee.evaluations);
                const basicCompleted = [
                  employee.evaluations.bizPlgEvaluation,
                  employee.evaluations.selfEvaluation,
                  employee.evaluations.firstEvaluation,
                  employee.evaluations.secondEvaluation,
                ].filter(v => v === "Y").length;
                const projectCompleted = employee.evaluations.projectEvaluations.filter(p => p.isCompleted === "Y").length;
                const totalCompleted   = basicCompleted + projectCompleted;
                const totalCount       = 4 + employee.evaluations.projectEvaluations.length;
                
                return (
                  <Col 
                    xxl={12}  // 큰 화면: 2열
                    xl={12}
                    lg={12}
                    md={12}
                    sm={24}   // 작은 화면: 1열
                    xs={24}
                    key={employee.evlId}
                  >
                    <Card 
                      size="small" 
                      styles={{ body: { padding: "12px" } }}
                      style={{ 
                        border: "2px solid #0050b3"
                      }}
                    >
                      {/* 피평가자 정보 (이름/직위/부서) */}
                      <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 8, flexWrap: "wrap" }}>
                        <Text strong style={{ fontSize: 14, whiteSpace: "nowrap" }}>{employee.name}</Text>
                        <Text type="secondary" style={{ fontSize: 11, whiteSpace: "nowrap" }}>{employee.position}</Text>
                        <Tag style={{ fontSize: 11, margin: 0, padding: "0 6px", border: "1px solid #d9d9d9", backgroundColor: "#fafafa" }}>{employee.department}</Tag>
                      </div>

                      {/* 평가 버튼들 */}
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        {/* 업무서약서와 자기평가  렌더링 */}
                        {evaluationKeys.slice(0, 2).map(({ key, type }) => {
                          const isComplete  = employee.evaluations[key];              // 완료 여부
                          const isAvailable = isEvaluationAvailable(type, employee);  // 작성 가능 여부 (이전 평가 완료 확인)
                          const evalName    = EVALUATION_TYPES[type];                 // 평가명
                          
                          // 업무서약서인지 확인 (폰트 크기 조정용)
                          const isWorkCommitment = type === "work-commitment";
                          const fontSize         = isWorkCommitment ? 12 : 13;
                          const padding          = isWorkCommitment ? "8.5px 10px" : "8px 10px";
                          
                          // ====================================
                          // 권한 확인
                          // ====================================
                          const isCurrentEmployee = loginEmpNo === employee.empNo;             // 본인 여부
                          const isFirstEvaluator  = loginEmpNo === employee.firstEvaluatorId;  // 1차 평가자 여부
                          const isSecondEvaluator = loginEmpNo === employee.secondEvaluatorId; // 2차 평가자 여부
                          
                          // ====================================
                          // 버튼 표시 로직 (새로운 상태 시스템)
                          // ====================================
                          let shouldShowButton = true;
                          let canEdit          = false; // 본인이 작성할 수 있는 평가인지 여부
                          
                          // PLG 기간에는 업무서약서만 표시
                          if (employee.hasHrEvaluation === 'N' && type !== 'work-commitment') {
                            shouldShowButton = false;
                          }
                          
                          // 관리자 모드: 모든 버튼 표시하되 readonly로 설정
                          if (isAdminMode) {
                            canEdit = false; // readonly 모드
                          } else {
                            // 본인일 경우: 업무서약서와 자기평가만 작성 가능
                            if (isCurrentEmployee) {
                              if (type === "work-commitment" || type === "self-evaluation") {
                                canEdit = true;
                              } else {
                                shouldShowButton = false;  // 1차/2차평가 버튼 숨김
                              }
                            }
                            // 1차 평가자일 경우
                            if (isFirstEvaluator && !isCurrentEmployee) {
                              if (type === "first-evaluation") {
                                canEdit = true;
                              }
                              // 업무서약서와 자기평가는 상태만 표시 (canEdit = false 유지)
                            }
                            // 2차 평가자일 경우
                            if (isSecondEvaluator && !isCurrentEmployee) {
                              if (type === "second-evaluation") {
                                canEdit = true;
                              }
                              // 업무서약서, 자기평가, 1차평가는 상태만 표시 (canEdit = false 유지)
                            }
                          }
                          
                          // 버튼 숨김 처리
                          if (!shouldShowButton) {
                            return null;
                          }

                          // 평가 상태 계산
                          const status = getEvaluationStatus(type, employee, isComplete);

                          // ====================================
                          // 상태별 버튼 렌더링
                          // ====================================
                          
                          // 1. 완료 상태
                          if (status === "completed") {
                            return (
                              <div 
                                key={key}
                                style={{ 
                                  border: "1px solid #91d5ff",
                                  borderRadius: 4,
                                  padding: padding,
                                  backgroundColor: "#e6f7ff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  cursor: "pointer",
                                  width: 125,
                                  transition: "all 0.2s"
                                }}
                                onClick={() => {
                                  const periodKey  = getEvaluationPeriodKey(type);
                                  const periods    = periodKey ? employee.evaluationPeriods[periodKey] : undefined;
                                  const isInPeriod = periods ? (currentDateStr >= periods.startDate && currentDateStr <= periods.endDate) : false;
                                  
                                  openEvlPage({
                                    evlId       : employee.evlId,
                                    evlType     : getEvlTypeString(type),
                                    empNo       : employee.empNo,
                                    evalPeriodYn: isInPeriod,
                                    evlStatus   : "completed"
                                  }, selectedYear, canEdit);
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(24, 144, 255, 0.1)";
                                  e.currentTarget.style.transform = "translateY(-2px)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.boxShadow = "none";
                                  e.currentTarget.style.transform = "translateY(0)";
                                }}
                              >
                                <Text style={{ fontSize: fontSize, fontWeight: 500 }}>{evalName}</Text>
                                <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                                  <CheckCircleOutlined style={{ color: "#1890ff", fontSize: 11 }} />
                                  <Text style={{ fontSize: 11 }}>완료</Text>
                                </div>
                              </div>
                            );
                          }

                          // 2. 작성 가능 상태
                          if (status === "editable") {
                            // 2-1. 본인이 작성할 수 있는 평가 (파란 버튼)
                            if (canEdit) {
                              return (
                                <div key={key} style={{ width: 125 }}>
                                  <div 
                                    style={{ 
                                      border: "2px solid #003a8c",
                                      borderRadius: 4,
                                      padding: padding,
                                      backgroundColor: "#1890ff",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      cursor: "pointer",
                                      transition: "all 0.2s",
                                      width: "100%"
                                    }}
                                    onClick={() => {
                                      const periodKey  = getEvaluationPeriodKey(type);
                                      const periods    = periodKey ? employee.evaluationPeriods[periodKey] : undefined;
                                      const isInPeriod = periods ? (currentDateStr >= periods.startDate && currentDateStr <= periods.endDate) : false;
                                      
                                      openEvlPage({
                                        evlId       : employee.evlId,
                                        evlType     : getEvlTypeString(type),
                                        empNo       : employee.empNo,
                                        evalPeriodYn: isInPeriod,
                                        evlStatus   : "in-progress"
                                      }, selectedYear, canEdit);
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.boxShadow = "0 2px 8px rgba(24, 144, 255, 0.2)";
                                        e.currentTarget.style.transform = "translateY(-2px)";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.boxShadow = "none";
                                        e.currentTarget.style.transform = "translateY(0)";
                                      }}
                                    >
                                      <Text style={{ fontSize: fontSize, fontWeight: 500, color: "#fff" }}>{evalName}</Text>
                                      <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                                        <EditOutlined style={{ color: "#fff", fontSize: 11 }} />
                                        <Text style={{ fontSize: 11, color: "#fff" }}>작성</Text>
                                      </div>
                                    </div>
                                </div>
                              );
                            }
                            // 2-2. 다른 사람이 작성해야 하는 평가 (진행중 표시)
                            else {
                              return (
                                <div 
                                  key={key}
                                  style={{ 
                                    border: "1px solid #d9d9d9",
                                    borderRadius: 4,
                                    padding: padding,
                                    backgroundColor: "#f5f5f5",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    cursor: "not-allowed",
                                    opacity: 0.6,
                                    width: 125
                                  }}
                                >
                                  <Text style={{ fontSize: fontSize, fontWeight: 500, color: "rgba(0, 0, 0, 0.45)" }}>{evalName}</Text>
                                  <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <StopOutlined style={{ fontSize: 11, color: "rgba(0, 0, 0, 0.45)" }} />
                                    <Text style={{ fontSize: 11, color: "rgba(0, 0, 0, 0.45)" }}>진행중</Text>
                                  </div>
                                </div>
                              );
                            }
                          }

                          // 3. 미작성 상태 (기한 지남 + 미제출)
                          if (status === "expired") {
                            return (
                              <div 
                                key={key}
                                style={{ 
                                  border: "1px solid #d9d9d9",
                                  borderRadius: 4,
                                  padding: padding,
                                  backgroundColor: "#f5f5f5",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  cursor: "not-allowed",
                                  opacity: 0.6,
                                  width: 125
                                }}
                              >
                                <Text style={{ fontSize: fontSize, fontWeight: 500, color: "rgba(0, 0, 0, 0.45)" }}>{evalName}</Text>
                                <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                                  <CloseOutlined style={{ fontSize: 11, color: "rgba(0, 0, 0, 0.45)" }} />
                                  <Text style={{ fontSize: 11, color: "rgba(0, 0, 0, 0.45)" }}>미작성</Text>
                                </div>
                              </div>
                            );
                          }

                          // 4. 진행중 상태 (기한 내 + 선행 조건 미충족)
                          if (status === "in-progress") {
                            return (
                              <div 
                                key={key}
                                style={{ 
                                  border: "1px solid #d9d9d9",
                                  borderRadius: 4,
                                  padding: padding,
                                  backgroundColor: "#f5f5f5",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  cursor: "not-allowed",
                                  opacity: 0.6,
                                  width: 125
                                }}
                              >
                                <Text style={{ fontSize: fontSize, fontWeight: 500, color: "rgba(0, 0, 0, 0.45)" }}>{evalName}</Text>
                                <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                                  <StopOutlined style={{ fontSize: 11, color: "rgba(0, 0, 0, 0.45)" }} />
                                  <Text style={{ fontSize: 11, color: "rgba(0, 0, 0, 0.45)" }}>진행중</Text>
                                </div>
                              </div>
                            );
                          }

                          // 5. 대기 상태 (평가 기간 미도래 OR 선행 조건 미충족)
                          if (status === "waiting") {
                            return (
                              <div 
                                key={key}
                                style={{ 
                                  border: "1px solid #d9d9d9",
                                  borderRadius: 4,
                                  padding: padding,
                                  backgroundColor: "#f5f5f5",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  cursor: "not-allowed",
                                  opacity: 0.6,
                                  width: 125
                                }}
                              >
                                <Text style={{ fontSize: fontSize, fontWeight: 500, color: "rgba(0, 0, 0, 0.45)" }}>{evalName}</Text>
                                <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                                  <LockOutlined style={{ fontSize: 11, color: "rgba(0, 0, 0, 0.45)" }} />
                                  <Text style={{ fontSize: 11, color: "rgba(0, 0, 0, 0.45)" }}>대기</Text>
                                </div>
                              </div>
                            );
                          }

                          return null;
                        })}

                        {/* 프로젝트평가 통합 버튼 (PLG 기간에는 표시 안 함) */}
                        {(() => {
                          // PLG 기간에는 프로젝트평가 버튼 표시 안 함
                          if (employee.period === 'PLG') {
                            return null;
                          }
                          
                          // 관리자 모드가 아닐 때만 본인 카드 체크
                          if (!isAdminMode) {
                            // 본인 카드인 경우 프로젝트평가 버튼을 표시하지 않음
                            const isCurrentEmployee = loginEmpNo === employee.empNo;
                            if (isCurrentEmployee) {
                              return null;
                            }
                          }
                          
                          // 관리자 모드가 아닐 때만 권한 체크
                          if (!isAdminMode) {
                            // 1차 또는 2차 평가자만 프로젝트평가 버튼을 볼 수 있음
                            const isFirstEvaluator  = loginEmpNo === employee.firstEvaluatorId;
                            const isSecondEvaluator = loginEmpNo === employee.secondEvaluatorId;
                            if (!isFirstEvaluator && !isSecondEvaluator) {
                              return null;
                            }
                          }
                          
                          // 피평가자가 받아야 하는 모든 프로젝트평가를 표시
                          const allProjects    = employee.evaluations.projectEvaluations;
                          const completedCount = allProjects.filter(p => p.isCompleted === "Y").length;
                          const totalCount     = allProjects.length;
                          const allCompleted   = totalCount > 0 && completedCount === totalCount;
                          
                          // 프로젝트평가가 없으면 버튼을 표시하지 않음
                          if (totalCount === 0) {
                            return null;
                          }
                          
                          return (
                            <div 
                              key="project-evaluation"
                              onClick={() => {
                                setSelectedEmployeeForModal(employee);
                                setProjectModalVisible(true);
                              }}
                              style={{ 
                                border: allCompleted ? "1px solid #91d5ff" : "1px solid #d9d9d9",
                                borderRadius: 4,
                                padding: "8px 10px",
                                backgroundColor: allCompleted ? "#e6f7ff" : "#f5f5f5",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                cursor: "pointer",
                                opacity: allCompleted ? 1 : 0.6,
                                width: 125,
                                transition: "all 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.opacity = "1";
                                e.currentTarget.style.transform = "translateY(-1px)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.opacity = allCompleted ? "1" : "0.6";
                                e.currentTarget.style.transform = "translateY(0)";
                              }}
                            >
                              <Text style={{ fontSize: 12, fontWeight: 500, color: allCompleted ? "#000" : "rgba(0, 0, 0, 0.45)" }}>
                                프로젝트평가
                              </Text>
                              <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                                {allCompleted ? (
                                  <>
                                    <CheckCircleOutlined style={{ color: "#1890ff", fontSize: 11 }} />
                                    <Text style={{ fontSize: 11 }}>완료</Text>
                                  </>
                                ) : (
                                  <>
                                    <Text style={{ fontSize: 11, color: "rgba(0, 0, 0, 0.45)" }}>
                                      {completedCount}/{totalCount}
                                    </Text>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })()}

                        {/* 1차평가와 2차평가 렌더링 (PLG 기간에는 표시 안 함) */}
                        {employee.period !== 'PLG' && evaluationKeys.slice(2).map(({ key, type }) => {
                          const isComplete = employee.evaluations[key];
                          const evalName   = EVALUATION_TYPES[type];
                          
                          const isCurrentEmployee = loginEmpNo === employee.empNo;
                          const isFirstEvaluator  = loginEmpNo === employee.firstEvaluatorId;
                          const isSecondEvaluator = loginEmpNo === employee.secondEvaluatorId;
                          
                          let shouldShowButton = true;
                          let canEdit          = false;
                          
                          // 관리자 모드: 모든 버튼 표시하되 readonly로 설정
                          if (isAdminMode) {
                            shouldShowButton = true;
                            canEdit          = false; // readonly 모드
                          } else {
                            // 본인: 1차/2차 평가 버튼 안 보임
                            if (isCurrentEmployee) {
                              shouldShowButton = false;
                            }
                            // 1차 평가자: 1차평가만 표시
                            else if (isFirstEvaluator) {
                              if (type === "first-evaluation") {
                                shouldShowButton = true;
                                canEdit          = true;
                              } else {
                                shouldShowButton = false; // 2차평가는 숨김
                              }
                            }
                            // 2차 평가자: 1차평가 조회 가능, 2차평가 작성 가능
                            else if (isSecondEvaluator) {
                              shouldShowButton = true;
                              if (type === "second-evaluation") {
                                canEdit = true; // 2차평가는 작성 가능
                              } else {
                                canEdit = false; // 1차평가는 조회만 가능
                              }
                            }
                            // 아무 권한도 없음: 버튼 숨김
                            else {
                              shouldShowButton = false;
                            }
                          }
                          
                          if (!shouldShowButton) {
                            return null;
                          }

                          // 평가 상태 계산
                          const status = getEvaluationStatus(type, employee, isComplete);

                          // 1. 완료 상태
                          if (status === "completed") {
                            return (
                              <div 
                                key={key}
                                style={{ 
                                  border: "1px solid #91d5ff",
                                  borderRadius: 4,
                                  padding: "8px 10px",
                                  backgroundColor: "#e6f7ff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  cursor: "pointer",
                                  width: 125,
                                  transition: "all 0.2s"
                                }}
                                onClick={() => {
                                  const periodKey  = getEvaluationPeriodKey(type);
                                  const periods    = periodKey ? employee.evaluationPeriods[periodKey] : undefined;
                                  const isInPeriod = periods ? (currentDateStr >= periods.startDate && currentDateStr <= periods.endDate) : false;
                                  
                                  openEvlPage({
                                    evlId       : employee.evlId,
                                    evlType     : getEvlTypeString(type),
                                    empNo       : employee.empNo,
                                    evalPeriodYn: isInPeriod,
                                    evlStatus   : "completed"
                                  }, selectedYear, canEdit);
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(24, 144, 255, 0.1)";
                                  e.currentTarget.style.transform = "translateY(-2px)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.boxShadow = "none";
                                  e.currentTarget.style.transform = "translateY(0)";
                                }}
                              >
                                <Text style={{ fontSize: 13, fontWeight: 500 }}>{evalName}</Text>
                                <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                                  <CheckCircleOutlined style={{ color: "#1890ff", fontSize: 11 }} />
                                  <Text style={{ fontSize: 11 }}>완료</Text>
                                </div>
                              </div>
                            );
                          }

                          // 2. 작성 가능 상태
                          if (status === "editable") {
                            // 2-1. 본인이 작성할 수 있는 평가 (파란 버튼)
                            if (canEdit) {
                              return (
                                <div key={key} style={{ width: 125 }}>
                                  <div 
                                    style={{ 
                                      border: "2px solid #003a8c",
                                      borderRadius: 4,
                                      padding: "8px 10px",
                                      backgroundColor: "#1890ff",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      cursor: "pointer",
                                      transition: "all 0.2s",
                                      width: "100%"
                                    }}
                                    onClick={() => {
                                      const periodKey  = getEvaluationPeriodKey(type);
                                      const periods    = periodKey ? employee.evaluationPeriods[periodKey] : undefined;
                                      const isInPeriod = periods ? (currentDateStr >= periods.startDate && currentDateStr <= periods.endDate) : false;
                                      
                                      openEvlPage({
                                        evlId       : employee.evlId,
                                        evlType     : getEvlTypeString(type),
                                        empNo       : employee.empNo,
                                        evalPeriodYn: isInPeriod,
                                        evlStatus   : "in-progress"
                                      }, selectedYear, canEdit);
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.boxShadow = "0 2px 8px rgba(24, 144, 255, 0.2)";
                                      e.currentTarget.style.transform = "translateY(-2px)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.boxShadow = "none";
                                      e.currentTarget.style.transform = "translateY(0)";
                                    }}
                                  >
                                    <Text style={{ fontSize: 13, fontWeight: 500, color: "#fff" }}>{evalName}</Text>
                                    <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                                      <EditOutlined style={{ color: "#fff", fontSize: 11 }} />
                                      <Text style={{ fontSize: 11, color: "#fff" }}>작성</Text>
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                            // 2-2. 다른 사람이 작성해야 하는 평가 (진행중 표시)
                            else {
                              return (
                                <div 
                                  key={key}
                                  style={{ 
                                    border: "1px solid #d9d9d9",
                                    borderRadius: 4,
                                    padding: "8px 10px",
                                    backgroundColor: "#f5f5f5",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    cursor: "not-allowed",
                                    opacity: 0.6,
                                    width: 125
                                  }}
                                >
                                  <Text style={{ fontSize: 13, fontWeight: 500, color: "rgba(0, 0, 0, 0.45)" }}>{evalName}</Text>
                                  <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <StopOutlined style={{ fontSize: 11, color: "rgba(0, 0, 0, 0.45)" }} />
                                    <Text style={{ fontSize: 11, color: "rgba(0, 0, 0, 0.45)" }}>진행중</Text>
                                  </div>
                                </div>
                              );
                            }
                          }

                          // 3. 미작성 상태
                          if (status === "expired") {
                            return (
                              <div 
                                key={key}
                                style={{ 
                                  border: "1px solid #d9d9d9",
                                  borderRadius: 4,
                                  padding: "8px 10px",
                                  backgroundColor: "#f5f5f5",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  cursor: "not-allowed",
                                  opacity: 0.6,
                                  width: 125
                                }}
                              >
                                <Text style={{ fontSize: 13, fontWeight: 500, color: "rgba(0, 0, 0, 0.45)" }}>{evalName}</Text>
                                <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                                  <CloseOutlined style={{ fontSize: 11, color: "rgba(0, 0, 0, 0.45)" }} />
                                  <Text style={{ fontSize: 11, color: "rgba(0, 0, 0, 0.45)" }}>미작성</Text>
                                </div>
                              </div>
                            );
                          }

                          // 4. 진행중 상태
                          if (status === "in-progress") {
                            return (
                              <div 
                                key={key}
                                style={{ 
                                  border: "1px solid #d9d9d9",
                                  borderRadius: 4,
                                  padding: "8px 10px",
                                  backgroundColor: "#f5f5f5",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  cursor: "not-allowed",
                                  opacity: 0.6,
                                  width: 125
                                }}
                              >
                                <Text style={{ fontSize: 13, fontWeight: 500, color: "rgba(0, 0, 0, 0.45)" }}>{evalName}</Text>
                                <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                                  <StopOutlined style={{ fontSize: 11, color: "rgba(0, 0, 0, 0.45)" }} />
                                  <Text style={{ fontSize: 11, color: "rgba(0, 0, 0, 0.45)" }}>진행중</Text>
                                </div>
                              </div>
                            );
                          }

                          // 5. 대기 상태
                          if (status === "waiting") {
                            return (
                              <div 
                                key={key}
                                style={{ 
                                  border: "1px solid #d9d9d9",
                                  borderRadius: 4,
                                  padding: "8px 10px",
                                  backgroundColor: "#f5f5f5",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  cursor: "not-allowed",
                                  opacity: 0.6,
                                  width: 125
                                }}
                              >
                                <Text style={{ fontSize: 13, fontWeight: 500, color: "rgba(0, 0, 0, 0.45)" }}>{evalName}</Text>
                                <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                                  <LockOutlined style={{ fontSize: 11, color: "rgba(0, 0, 0, 0.45)" }} />
                                  <Text style={{ fontSize: 11, color: "rgba(0, 0, 0, 0.45)" }}>대기</Text>
                                </div>
                              </div>
                            );
                          }

                          return null;
                        })}
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </Row>
                  </Space>
                ),
              },
              {
                key: "project",
                label: "프로젝트평가",
                children: (
                  <Space direction="vertical" style={{ width: "100%" }} size={8}>
                    {/* ====================================
                        프로젝트평가 탭 통계 카드
                        ==================================== */}
                    <div style={{ display: "flex", gap: 8 }}>
                      <StatsCard
                        title="프로젝트평가"
                        completed={myProjectStats.completedProjects}
                        total={myProjectStats.totalProjects}
                        deadline={evaluationDeadlines["project-evaluation"]}
                      />
                      {/* 빈 공간 추가로 인사평가 탭과 동일한 크기 유지 */}
                      <div style={{ flex: "1 1 0", minWidth: 160 }} />
                      <div style={{ flex: "1 1 0", minWidth: 160 }} />
                      <div style={{ flex: "1 1 0", minWidth: 160 }} />
                      <div style={{ flex: "1 1 0", minWidth: 160 }} />
                    </div>

            {/* ====================================
                프로젝트 평가 카드
                ==================================== 
                - 프로젝트명별로 그룹화
                - 한 줄에 최대 8명까지 표시
                - 녹색 테두리로 인사평가와 구분
            */}
            {Object.entries(projectGroups)
              .sort(([, itemsA], [, itemsB]) => {
                // 1차 정렬: 고객사명 가나다순
                const customerA       = itemsA[0]?.project.customerName || '';
                const customerB       = itemsB[0]?.project.customerName || '';
                const customerCompare = customerA.localeCompare(customerB, 'ko-KR');
                if (customerCompare !== 0) return customerCompare;
                
                // 2차 정렬: 프로젝트명 가나다순
                const projectA = itemsA[0]?.project.pjNm || '';
                const projectB = itemsB[0]?.project.pjNm || '';
                return projectA.localeCompare(projectB, 'ko-KR');
              })
              .map(([projectName, items]) => (
              <Card 
                key={projectName} 
                size="small" 
                styles={{ body: { padding: "12px" } }}
                style={{ border: "2px solid #389e0d" }}  // 녹색 테두리
              >
                {/* 프로젝트명 헤더 */}
                <div style={{ marginBottom: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: windowWidth <= 1000 ? "wrap" : "nowrap" }}>
                  <Tag style={{ margin: 0, backgroundColor: "#52c41a", color: "#ffffff", border: "none" }}>{items[0].project.customerName}</Tag>
                  <div>
                    <Text strong style={{ fontSize: 14 }}>{projectName}</Text>
                    <Text type="secondary" style={{ fontSize: 11, marginLeft: 4, whiteSpace: "nowrap" }}>
                      ({items.filter(item => item.project.isCompleted === "Y").length}/{items.length})
                    </Text>
                  </div>
                </div>
                
                {/* 프로젝트 참여자 목록 */}
                <Row gutter={[8, 8]}>
                  {items.sort((a, b) => a.employee.name.localeCompare(b.employee.name, 'ko-KR')).map(({ employee, project }) => {
                    // 프로젝트별 평가 기간 체크
                    const projectStartDate  = project.startDate ? new Date(project.startDate) : null;
                    const projectEndDate    = project.endDate   ? new Date(project.endDate)   : null;
                    const isInProjectPeriod = projectStartDate && projectEndDate ? 
                      (currentDate >= projectStartDate && currentDate <= projectEndDate) : false;
                    const isCompleted       = project.isCompleted === "Y";
                    const isPastDue         = projectEndDate ? currentDate > projectEndDate : false;
                    
                    // 관리자 모드: 미완료 건은 기한에 따라 "작성중" 또는 "미완료"로 표시, 작성 불가
                    // 일반 모드: 완료 OR 기한 내 작성 가능
                    const isDisabled = isAdminMode ? !isCompleted : (!isCompleted && isPastDue);
                    
                    return (
                    <Col 
                      key={`${employee.evlId}-${project.evlId}`}
                      style={{ minWidth: 160, flex: "1 1 auto", maxWidth: "12.5%" }}
                    >
                      <div 
                        style={{ 
                          border: isCompleted ? "1px solid #95de64" : isDisabled ? "1px solid #d9d9d9" : "2px solid #389e0d", 
                          borderRadius: 4, 
                          padding: "8px 10px",
                          backgroundColor: isCompleted ? "#f6ffed" : isDisabled ? "#f5f5f5" : "#52c41a",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          cursor: isDisabled ? "not-allowed" : (isCompleted || !isAdminMode) ? "pointer" : "not-allowed",
                          opacity: isDisabled ? 0.6 : 1,
                          transition: "all 0.2s"
                        }}
                        onClick={() => {
                          if (isDisabled) return; // 관리자 모드에서 미완료 건 OR 일반 모드에서 기한 지남 + 미제출 시 클릭 불가
                          
                          // 관리자 모드: 완료 건만 readonly로 열기
                          // 일반 모드: 프로젝트평가는 항상 작성 가능 (본인이 평가하는 것으로 간주)
                          const canEdit = isAdminMode ? false : true;
                          
                          openEvlPage({
                            evlId       : project.evlId,
                            evlType     : project.evlType,  // pjEvlType 값 사용 (03: PM, 04: 팀원)
                            empNo       : employee.empNo,
                            pjNm        : project.pjNm,
                            pjCd        : project.pjCd,
                            evalPeriodYn: isInProjectPeriod,
                            evlStatus   : isCompleted ? "completed" : "in-progress",
                            pjPmEmpNo   : project.evaluatorId  // 프로젝트평가자 empNo 추가
                          }, selectedYear, canEdit);
                        }}
                        onMouseEnter={(e) => {
                          if (!isDisabled) {
                            e.currentTarget.style.boxShadow = "0 2px 8px rgba(24, 144, 255, 0.2)";
                            e.currentTarget.style.transform = "translateY(-2px)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isDisabled) {
                            e.currentTarget.style.boxShadow = "none";
                            e.currentTarget.style.transform = "translateY(0)";
                          }
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, minWidth: 0 }}>
                          <Text strong style={{ fontSize: 13, fontWeight: 500, color: isCompleted ? "#000" : isDisabled ? "rgba(0, 0, 0, 0.45)" : "#fff" }}>
                            {employee.name}
                          </Text>
                          <Text style={{ fontSize: 11, color: isCompleted ? "rgba(0, 0, 0, 0.45)" : isDisabled ? "rgba(0, 0, 0, 0.25)" : "#fff" }}>{employee.position}</Text>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
                          {isCompleted ? (
                            <>
                              <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 11 }} />
                              <Text type="secondary" style={{ fontSize: 11 }}>완료</Text>
                            </>
                          ) : isDisabled ? (
                            <>
                              <CloseCircleOutlined style={{ color: "rgba(0, 0, 0, 0.25)", fontSize: 11 }} />
                              <Text type="secondary" style={{ fontSize: 11 }}>{isPastDue ? "미완료" : "작성중"}</Text>
                            </>
                          ) : (
                            <>
                              <EditOutlined style={{ color: "#fff", fontSize: 11 }} />
                              <Text style={{ fontSize: 11, color: "#fff" }}>작성</Text>
                            </>
                          )}
                        </div>
                      </div>
                    </Col>
                  );
                  })}
                </Row>
              </Card>
            ))}
                  </Space>
                ),
              },
            ]}
          />
        )}

        {/* 프로젝트평가 상세 모달 */}
        <Modal
          title={
            <div>
              <Text strong>{selectedEmployeeForModal?.name}</Text>
              <Text type="secondary" style={{ marginLeft: 8, fontSize: 13 }}>
                님의 프로젝트평가 목록
              </Text>
            </div>
          }
          open={projectModalVisible}
          onCancel={() => {
            setProjectModalVisible(false);
            setSelectedEmployeeForModal(null);
          }}
          footer={[
            <Button 
              key="close" 
              onClick={() => {
                setProjectModalVisible(false);
                setSelectedEmployeeForModal(null);
              }}
            >
              닫기
            </Button>
          ]}
          width={600}
        >
          {selectedEmployeeForModal && (
            <div style={{ marginTop: 16 }}>
              {selectedEmployeeForModal.evaluations.projectEvaluations.length === 0 ? (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <Text type="secondary">프로젝트평가가 없습니다.</Text>
                </div>
              ) : (
                <Space direction="vertical" style={{ width: "100%" }} size={12}>
                  {selectedEmployeeForModal.evaluations.projectEvaluations.map((project) => {
                    // 프로젝트에 직접 저장된 평가자 정보가 있으면 그것을 사용, 없으면 evaluatorId로 조회
                    const evaluatorInfo = project.evaluatorName 
                      ? {
                          name      : project.evaluatorName,
                          position  : project.evaluatorPosition || "",
                          department: project.evaluatorDepartment || ""
                        }
                      : getEvaluatorInfo(project.evaluatorId || "");
                    
                    // 프로젝트별 평가 기간 체크
                    const projectStartDate   = project.startDate ? new Date(project.startDate) : null;
                    const projectEndDate     = project.endDate ? new Date(project.endDate) : null;
                    const isInProjectPeriod  = projectStartDate && projectEndDate ? 
                      (currentDate >= projectStartDate && currentDate <= projectEndDate) : false;
                    const isCompleted        = project.isCompleted === "Y";
                    const isPastDue          = projectEndDate ? currentDate > projectEndDate : false;
                    
                    // 프로젝트 평가자 권한 확인
                    const isProjectEvaluator = project.evaluatorId === loginEmpNo || !project.evaluatorId;
                    
                    // 상태 결정: 완료, 작성 가능, 미작성 (기한 지남), 권한 없음
                    const isDisabled         = !isCompleted && (isPastDue || !isProjectEvaluator);
                    
                    return (
                      <Card 
                        key={project.evlId} 
                        size="small"
                        style={{ 
                          border: isCompleted ? "1px solid #91d5ff" : isDisabled ? "1px solid #d9d9d9" : "1px solid #d9d9d9",
                          backgroundColor: isCompleted ? "#e6f7ff" : isDisabled ? "#f5f5f5" : "#ffffff",
                          cursor: isDisabled ? "not-allowed" : "pointer",
                          opacity: isDisabled ? 0.6 : 1,
                          transition: "all 0.2s"
                        }}
                        onClick={() => {
                          if (isDisabled) return; // 기한 지남 또는 권한 없음 + 미제출 시 클릭 불가
                          
                          // 프로젝트평가자인 경우만 작성 가능
                          const canEdit = isAdminMode ? false : true;
                          openEvlPage({
                            evlId       : project.evlId,
                            evlType     : project.evlType,  // pjEvlType 값 사용 (03: PM, 04: 팀원)
                            empNo       : selectedEmployeeForModal.empNo,
                            pjNm        : project.pjNm,
                            pjCd        : project.pjCd,
                            evalPeriodYn: isInProjectPeriod,
                            evlStatus   : isCompleted ? "completed" : "in-progress",
                            pjPmEmpNo   : project.evaluatorId
                          }, selectedYear, canEdit);
                          
                          setProjectModalVisible(false);
                          setSelectedEmployeeForModal(null);
                        }}
                        onMouseEnter={(e) => {
                          if (!isDisabled) {
                            e.currentTarget.style.boxShadow = "0 2px 8px rgba(24, 144, 255, 0.2)";
                            e.currentTarget.style.transform = "translateY(-2px)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isDisabled) {
                            e.currentTarget.style.boxShadow = "none";
                            e.currentTarget.style.transform = "translateY(0)";
                          }
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
                            {/* 고객사명 */}
                            {project.customerName && (
                              <div>
                                <Tag style={{ fontSize: 13, padding: "2px 8px", backgroundColor: "#1890ff", color: "#fff", border: "none" }}>
                                  {project.customerName}
                                </Tag>
                              </div>
                            )}
                            
                            {/* 프로젝트명 */}
                            <div>
                              <Text strong style={{ fontSize: 14 }}>
                                {project.pjNm}
                              </Text>
                            </div>
                            
                            {/* 평가자 정보 */}
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <Text style={{ fontSize: 13 }}>
                                {evaluatorInfo.name}
                              </Text>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {evaluatorInfo.position}
                              </Text>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                · {evaluatorInfo.department}
                              </Text>
                            </div>
                          </div>
                          
                          {/* 완료 상태 */}
                          <div>
                            {isCompleted ? (
                              <Tag color="blue" icon={<CheckCircleOutlined />}>
                                완료
                              </Tag>
                            ) : isDisabled ? (
                              <Tag color="default" icon={<CloseCircleOutlined />}>
                                미작성
                              </Tag>
                            ) : (
                              <Tag color="green">
                                미완료
                              </Tag>
                            )}
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </Space>
              )}
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default Evl0101m;